<template>
    <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
        <page-header :subtitle="pageSubtitle" :title="pageTitle" />

        <content-box :loading="loading">
            <template #header>
                <import-steps :path-prefix="pathPrefix" />
            </template>
            <template #body>
                <div class="divide-y">
                    <div
                        class="flex items-center border-gray-200 px-8 py-8"
                        :key="column.name"
                        v-for="column in matchedColumns"
                    >
                        <div class="flex-2">
                            <div class="rounded border border-gray-200 shadow-lg bg-white">
                                <div class="flex items-center px-4 py-3 border-b border-gray-200 text-base">
                                    <nio-icon class="mr-3" name="regular/list-bullets-1" />

                                    <span class="mr-2">
                                        {{ $t('imports.columnMatching.columnName') }}
                                    </span>

                                    <span class="py-1 px-2 rounded font-mono text-sm bg-gray-200">
                                        {{ column.name }}
                                    </span>
                                </div>

                                <div class="my-1 font-thin">
                                    <div
                                        class="flex items-center px-4 py-1"
                                        :key="rowIndex"
                                        v-for="(row, rowIndex) in limitedPreviewRows"
                                    >
                                        <nio-icon class="mr-2 text-xs" name="regular/arrow-right-1" />
                                        <div class="font-mono">
                                            {{ row[column.name] }}
                                        </div>
                                    </div>

                                    <div class="flex items-center px-4 py-1">
                                        <span class="text-xs font-mono">
                                            ···
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <nio-icon class="flex-1 text-3xl text-gray-600" name="regular/arrow-right-1" />

                        <div class="flex-2 flex flex-col items-center justify-center space-y-4">
                            <h3 class="text-xl">
                                {{ $t('imports.columnMatching.belongsTo') }}
                            </h3>

                            <nio-select
                                :button-class-name="column.value && column.value != 'none' ? 'border-blue-500' : ''"
                                class="w-3/4"
                                :options="fieldOptions"
                                v-model="column.value"
                            />
                            <div class="w-3/4" v-if="column.value == 'new'">
                                <nio-input v-model="column.placeholder" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex-1 flex items-center justify-end p-4 space-x-2">
                    <nio-button icon="regular/remove" @click="cancel">
                        {{ $t('global.general.cancel') }}
                    </nio-button>

                    <nio-button color="blue" icon="regular/check-double-1" @click="preview">
                        {{ $t('imports.columnMatching.btnPreview') }}
                    </nio-button>
                </div>
            </template>
        </content-box>

        <confirm-modal
            :opened.sync="openLeave"
            :title="$t('imports.confirmLeavingModal.header')"
            @confirm="resumeLeavePage"
        >
            <p slot="message">
                {{ $t('imports.confirmLeavingModal.message') }}
            </p>
        </confirm-modal>
    </div>
</template>

<script>
    import cloneDeep from 'lodash/cloneDeep.js';
    import { mapState, mapMutations } from 'vuex';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ImportSteps from '@/components/ImportSteps.vue';
    import PageHeader from '@/components/PageHeader.vue';

    export default {
        components: {
            ConfirmModal,
            ContentBox,
            ImportSteps,
            PageHeader,
        },

        props: {
            importType: {
                type: String,
                required: true,
            },
            pathPrefix: {
                type: String,
                required: true,
            },
            pageSubtitle: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                loading: true,
                nextPath: '',
                preventQuitting: true,
                matchedColumns: [],
                openLeave: false,
            };
        },

        computed: {
            ...mapState('appointment', {
                previewRows: state => state.previewRows,
                mainMatchedColumns: state => state.matchedColumns,
            }),

            pageTitle() {
                return this.$t(`imports.pageTitle.${this.pathPrefix}`);
            },

            limitedPreviewRows() {
                if (empty(this.previewRows)) {
                    const loadingRows = [];

                    for (let i = 0; i < 5; i++) {
                        loadingRows.push({
                            name: 'John Doe',
                            phone: '514 806-0000',
                            date: moment().format('YYYY/MM/DD'),
                        });
                    }

                    return loadingRows;
                }

                return this.previewRows.slice(0, 5);
            },

            fieldOptions() {
                return [
                    {
                        value: 'none',
                        icon: 'regular/delete',
                        dividedBottom: true,
                    },
                    {
                        value: 'new',
                        icon: 'regular/add',
                        dividedBottom: true,
                        type: 'campaign',
                    },
                    {
                        value: 'firstName',
                        icon: 'regular/single-neutral',
                    },
                    {
                        value: 'lastName',
                        icon: 'regular/single-neutral',
                    },
                    {
                        value: 'fullName',
                        icon: 'regular/single-neutral',
                    },
                    {
                        value: 'phone',
                        icon: 'regular/phone',
                    },
                    {
                        value: 'email',
                        icon: 'regular/envelope',
                    },
                    {
                        value: 'date',
                        icon: 'regular/calendar-3',
                        type: 'appointment',
                    },
                    {
                        value: 'time',
                        icon: 'regular/calendar-3',
                        type: 'appointment',
                    },
                    {
                        value: 'datetime',
                        icon: 'regular/calendar-3',
                        type: 'appointment',
                    },
                    {
                        value: 'appointmentNotes',
                        icon: 'regular/notes-paper-text',
                        type: 'appointment',
                    },
                    {
                        value: 'contactNotes',
                        icon: 'regular/notes-paper-text',
                    },
                    {
                        value: 'locale',
                        icon: 'regular/earth-1',
                    },
                    {
                        value: 'unsubscribed',
                        icon: 'regular/remove',
                    },
                    {
                        value: 'communicationPreference',
                        icon: 'regular/messages-bubble-heart',
                    },
                ].map(option => {
                    return {
                        ...option,
                        label: this.$t(`imports.columnMatching.${option.value}`),
                        disabled: this.itemDisabled(option.value),
                    };
                }).filter(option => {
                    return !option.type || option.type == this.importType;
                });
            },
        },

        methods: {
            ...mapMutations('campaign', [
                'clearSelectedCampaign',
            ]),

            ...mapMutations('appointment', [
                'setCurrentImport',
                'setPreviewRows',
                'setMatchedColumns',
            ]),

            isImportType(type) {
                return this.importType == type;
            },

            initMatchedColumns() {
                if (!empty(this.mainMatchedColumns)) {
                    this.matchedColumns = cloneDeep(this.mainMatchedColumns);
                    return;
                }

                this.matchedColumns = Object.keys(this.previewRows[0]).map(key => {
                    return {
                        name: key.length ? key : 'none',
                        value: 'none',
                        placeholder: key,
                    };
                });
            },

            itemDisabled(item) {
                if (['none', 'new'].includes(item)) {
                    return false;
                }

                return this.matchedColumns.some(column => {
                    return column.value == item;
                });
            },

            cancel() {
                this.$emit('cancel');
            },

            preview() {
                this.setMatchedColumns(this.matchedColumns);
                this.preventQuitting = false;

                this.$nextTick(() => {
                    this.$emit('preview');
                });
            },

            resumeLeavePage() {
                this.preventQuitting = false;

                if (this.importType == 'campaign') {
                    this.clearSelectedCampaign();
                }

                this.$router.push(this.nextPath);
            },

            async onBeforeRouteEnter(from) {
                // Reset values
                this.loading = true;
                this.nextPath = '';
                this.preventQuitting = true;
                this.openLeave = false;

                if (from.name != `${this.pathPrefix}.import.preview`) {
                    this.setMatchedColumns([]);
                }

                if (!empty(this.previewRows)) {
                    this.loading = false;
                    this.initMatchedColumns();
                    return;
                }

                this.matchedColumns = [
                    {
                        name: 'name',
                        value: 'none',
                        placeholder: '',
                    },
                    {
                        name: 'phone',
                        value: 'none',
                        placeholder: '',
                    },
                    {
                        name: 'date',
                        value: 'none',
                        placeholder: '',
                    },
                ];

                try {
                    const response = await this.$axios.get(`imports/${this.$route.params.id}`);
                    const data = response.data.data;

                    this.setCurrentImport(data.import);
                    this.setPreviewRows(data.previewRows);

                    this.$nextTick(() => {
                        this.loading = false;
                        this.initMatchedColumns();
                    });
                } catch (error) {
                    // Import deleted, redirect
                    if (error.response && error.response.status == 404) {
                        this.preventQuitting = false;

                        this.$emit('not-found');
                    } else {
                        throw error;
                    }
                }
            },

            onBeforeRouteLeave(to, next) {
                if (this.preventQuitting && to.name != `${this.pathPrefix}.import.match`) {
                    this.nextPath = to.path;
                    this.openLeave = true;
                } else {
                    this.preventQuitting = true;
                    next();
                }
            },
        },
    };
</script>
